import { useEffect, useState } from "react";
import { UserCircle, CaretDown, X, Check } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { listOrgAdmins, updateInquiryAssignee } from "@/lib/inquiries";
import useFocusTrap from "@/lib/hooks/useFocusTrap";

/**
 * Admin-only inquiry assignee picker.
 *
 * Renders a pill-shaped button; clicking it opens a lightweight popover
 * with the current admin first, other admins next, and an "Unassign"
 * action when currently assigned. Keeping this as a popover (not a
 * modal) so it feels native on both mobile and desktop — the picker
 * sits inside an article card and positions right below the trigger.
 *
 * Design:
 *   * The list of admins comes from rpc_list_admin_recipients (Module 10).
 *     We filter to the caller's org via the admin's profile.organization_id.
 *   * "Assign to me" is surfaced first for the happy path (most owners
 *     claim their own leads).
 *   * Writes hit the enrollment_inquiries UPDATE RLS policy directly —
 *     no new migration needed; the `assigned_to` column already exists.
 */
export default function InquiryAssigneePicker({
  inquiryId,
  currentAssignee, // { profile_id, full_name, email } | null
  onChange,
  disabled,
}) {
  const { profile } = useAuth();
  const [open, setOpen] = useState(false);
  const [admins, setAdmins] = useState(null);
  const [saving, setSaving] = useState(false);
  // Trap keyboard focus within the popover while it's open, and close on Escape.
  const popoverRef = useFocusTrap({ active: open, onEscape: () => setOpen(false) });

  useEffect(() => {
    if (!open || admins !== null) return;
    let alive = true;
    (async () => {
      const list = await listOrgAdmins(profile?.organization_id);
      if (alive) setAdmins(list);
    })();
    return () => { alive = false; };
  }, [open, admins, profile?.organization_id]);

  async function setAssignee(nextId) {
    if (saving) return;
    setSaving(true);
    try {
      const updated = await updateInquiryAssignee(inquiryId, nextId);
      onChange?.(updated);
      toast.success(nextId ? "Assigned" : "Unassigned");
      setOpen(false);
    } catch (err) {
      toast.error(err?.message || "Could not update assignee");
    } finally {
      setSaving(false);
    }
  }

  const assigneeLabel = currentAssignee?.full_name || "Unassigned";
  const assigneeInitial = (assigneeLabel[0] || "?").toUpperCase();
  const isMe = currentAssignee?.profile_id === profile?.id;

  return (
    <div className="relative" data-testid="inquiry-assignee-picker">
      <button
        type="button"
        data-testid="inquiry-assignee-trigger"
        data-assigned={!!currentAssignee}
        data-is-me={isMe}
        disabled={disabled || saving}
        onClick={() => setOpen((v) => !v)}
        className={`flex w-full items-center gap-2.5 rounded-2xl border px-3 py-2 text-left text-sm transition ${
          currentAssignee
            ? "border-primary/30 bg-primary/5 text-neutral-900"
            : "border-dashed border-neutral-300 bg-white text-neutral-600 hover:bg-neutral-50"
        } disabled:opacity-60`}
      >
        <span
          className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
            currentAssignee
              ? "bg-primary text-white"
              : "bg-neutral-100 text-neutral-400"
          }`}
        >
          {currentAssignee
            ? assigneeInitial
            : <UserCircle size={14} weight="duotone" />
          }
        </span>
        <span className="min-w-0 flex-1 truncate font-medium">
          {assigneeLabel}
          {isMe && (
            <span className="ml-1.5 rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-primary-hover">
              You
            </span>
          )}
        </span>
        <CaretDown size={12} weight="bold" className="text-neutral-400" />
      </button>

      {open && (
        <>
          {/* Click-outside backdrop — transparent, but covers the viewport
             so tapping elsewhere closes the popover. On mobile that matters. */}
          <button
            type="button"
            aria-label="Close picker"
            className="fixed inset-0 z-40 cursor-default"
            onClick={() => setOpen(false)}
          />
          <div
            ref={popoverRef}
            role="listbox"
            aria-label="Assignees"
            data-testid="inquiry-assignee-popover"
            className="absolute left-0 right-0 z-50 mt-2 max-h-72 overflow-y-auto rounded-2xl border border-neutral-200 bg-white p-1.5 shadow-lg"
          >
            {/* Self (unless already assigned to me) */}
            {profile?.id && !isMe && (
              <OptionRow
                testid="inquiry-assignee-option-self"
                label={`Assign to me${profile.full_name ? ` (${profile.full_name})` : ""}`}
                initial={(profile.full_name?.[0] || "M").toUpperCase()}
                onClick={() => setAssignee(profile.id)}
                highlight
              />
            )}

            {admins === null ? (
              <div className="px-3 py-4 text-center text-xs text-neutral-500">
                Loading admins…
              </div>
            ) : admins.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-neutral-500">
                No other admins in your organization yet.
              </div>
            ) : (
              admins
                .filter((a) => a.profile_id !== profile?.id)
                .map((a) => {
                  const selected = currentAssignee?.profile_id === a.profile_id;
                  return (
                    <OptionRow
                      key={a.profile_id}
                      testid={`inquiry-assignee-option-${a.profile_id}`}
                      label={a.full_name}
                      sub={a.email}
                      initial={(a.full_name[0] || "?").toUpperCase()}
                      selected={selected}
                      onClick={() => setAssignee(a.profile_id)}
                    />
                  );
                })
            )}

            {currentAssignee && (
              <>
                <div className="mx-2 my-1.5 h-px bg-neutral-100" />
                <OptionRow
                  testid="inquiry-assignee-option-clear"
                  label="Unassign"
                  icon={X}
                  destructive
                  onClick={() => setAssignee(null)}
                />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function OptionRow({ testid, label, sub, initial, icon: Icon, onClick, selected, highlight, destructive }) {
  return (
    <button
      type="button"
      role="option"
      aria-selected={!!selected}
      data-testid={testid}
      onClick={onClick}
      className={`flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm transition ${
        destructive
          ? "text-[#C6851E] hover:bg-[#C6851E]/10"
          : selected
            ? "bg-primary/10 text-neutral-900"
            : highlight
              ? "bg-primary/5 text-neutral-900 hover:bg-primary/10"
              : "text-neutral-800 hover:bg-neutral-50"
      }`}
    >
      <span
        className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-bold ${
          destructive
            ? "bg-[#C6851E]/10 text-[#C6851E]"
            : selected
              ? "bg-primary text-white"
              : "bg-neutral-100 text-neutral-700"
        }`}
      >
        {Icon ? <Icon size={14} weight="bold" /> : initial}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{label}</span>
        {sub && (
          <span className="block truncate text-[11px] text-neutral-500">{sub}</span>
        )}
      </span>
      {selected && <Check size={13} weight="bold" className="text-primary-hover" />}
    </button>
  );
}
